import {Component, EventEmitter, inject, Input, Output, SimpleChanges, ViewChild} from '@angular/core';
import {CapitalCasePipe} from "../../../../pipes/capital-case.pipe";
import {AsyncPipe, DatePipe, NgClass} from "@angular/common";
import {
    DxButtonComponent,
    DxFileUploaderComponent, DxPopupComponent, DxSelectBoxComponent, DxTextAreaComponent, DxTextBoxComponent,
    DxValidationGroupComponent, DxValidatorComponent
} from "devextreme-angular";
import {DxiPopupToolbarItemComponent} from "devextreme-angular/ui/popup";
import {DxiValidationRuleComponent} from "devextreme-angular/ui/nested";
import {PicturedItemSelectBoxComponent} from "../../../../components";
import {
    AuthService,
    ChangeRequestViewDto,
    ChangeRequestsClient,
    ChangeRequestStatus,
    ChangeRequestType,
    CreateChangeRequestCommand,
    DataSources,
    EmployeeDto,
    ScreenService,
    UploadedFileDto, ICurrentValuesDto, PermissionType, AccessType,
} from "@services";
import notify from "devextreme/ui/notify";
import EnumUtils from "../../../../shared/enum-utils";
import {BehaviorSubject, Observable, of, combineLatest} from "rxjs";
import {distinctUntilChanged, switchMap, map, shareReplay} from "rxjs/operators";
import {SignedLinkDirective} from 'src/app/directives/signed-link.directive';
import { FileUtils } from 'src/app/shared/file-utils';

@Component({
    selector: 'app-change-request-popup',
    imports: [
        CapitalCasePipe,
        DatePipe,
        DxButtonComponent,
        DxPopupComponent,
        DxValidationGroupComponent,
        DxiPopupToolbarItemComponent,
        DxFileUploaderComponent,
        DxTextAreaComponent,
        DxValidatorComponent,
        DxiValidationRuleComponent,
        PicturedItemSelectBoxComponent,
        AsyncPipe,
        NgClass,
        DxTextBoxComponent,
        DxSelectBoxComponent,
        SignedLinkDirective
    ],
    templateUrl: './change-request-popup.component.html',
    styleUrl: './change-request-popup.component.scss',
})
export class ChangeRequestPopupComponent {
    protected dataSources = inject(DataSources);
    protected screen = inject(ScreenService);
    protected changeRequestsClient = inject(ChangeRequestsClient);
    protected auth = inject(AuthService);

    @Input() request: ChangeRequestViewDto;
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onAdd = new EventEmitter<void>();
    @Output() onStatusChange = new EventEmitter<ChangeRequestStatus>();

    @ViewChild('validationGroup', {static: true}) validationGroup: DxValidationGroupComponent;

    employeesDataSource = this.dataSources.employeeOptions();
    formData: ChangeRequestViewDto | null = null;
    canShowInSelectBox: boolean
    ChangeRequestStatus = ChangeRequestStatus;
    typeOfChangeOptions = EnumUtils.getEnumOptions(ChangeRequestType);
    ChangeRequestType = ChangeRequestType;
    employeeId: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    changeRequestType: BehaviorSubject<ChangeRequestType | null> = new BehaviorSubject<ChangeRequestType | null>(null);

    fetchedValues$: Observable<ICurrentValuesDto | null> = this.employeeId.pipe(
        distinctUntilChanged(),
        switchMap(id => {
            if (id) {
                return this.changeRequestsClient.getCurrentValues(id);
            }
            return of(null);
        }),
        shareReplay(1)
    );

    oldValue: Observable<string> = combineLatest([this.fetchedValues$, this.changeRequestType]).pipe(
        map(([values, type]) => {
            if (!values || !type) return "";

            switch (type) {
                case ChangeRequestType.Email:
                    return values.email ?? "";
                case ChangeRequestType.PhoneNumber:
                    return values.phone ?? "";
                case ChangeRequestType.Address:
                    return values.address ?? "";
                default:
                    return "";
            }
        })
    );

    async ngOnInit(): Promise<void> {
        this.canShowInSelectBox = !!await this.employeesDataSource.byKey(this.auth.userId);
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['visible'] && changes['visible'].currentValue) {
            await this.loadData();
        }
    }

    onClose() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
        this.formData = null;
        this.validationGroup?.instance.reset();
    }

    handleSuccess() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
        this.onAdd.emit();
    }

    handleError(errors: any) {
        errors.forEach(((errMsg: any) => {
            notify({
                message: errMsg.message,
                type: "error",
                displayTime: 5000,
            }, {
                position: "bottom right",
                direction: "up-push"
            });
        }));
    }

    protected get canEdit(): boolean {
        return this.formData?.status === undefined;
    };

    protected get canEditEmployee(): boolean{
        return this.auth.checkPermission(PermissionType.InformationChangeRequestsStatusChange, AccessType.Manage) &&
            this.formData?.status === undefined;
    }

    protected onEmployeeChange(id: string) {
        this.formData.employee.id = id;
        this.employeeId.next(id);
    }

    protected onTypeChange(e: any) {
        this.changeRequestType.next(e.value);
    }

    files: File[] = [];

    async onSave() {
        const result = this.validationGroup.instance.validate();
        if (!result.isValid)
            return;

        const uploadedFiles = await FileUtils.mapFilesToDto(this.files);

        let command = new CreateChangeRequestCommand({
            userId: this.formData.employee.id,
            reasonForChange: this.formData.reasonForChange,
            institutionNumber : this.formData.institutionNumber,
            transitNumber : this.formData.transitNumber,
            accountNumber : this.formData.accountNumber,
            uploadedFile: uploadedFiles.length > 0 ? uploadedFiles[0] : null,
            typeOfChange: this.formData.typeOfChange,
            newValue: this.formData.newValue
        });

        this.changeRequestsClient.createChangeRequest(command).subscribe({
            next: () => this.handleSuccess(),
            error: (errors) => this.handleError(errors)
        });
    };

    async loadData() {
        if (this.request) {
            this.changeRequestsClient.viewChangeRequest(this.request.id).subscribe({
                next: (data) => {
                    this.formData = data;
                    this.employeeId.next(this.formData.employee.id);
                    this.changeRequestType.next(this.formData.typeOfChange);
                },
                error: (errors) => this.handleError(errors)
            });

        } else {
            this.formData = new ChangeRequestViewDto();
            const employeeId = this.canShowInSelectBox ? this.auth.userId : null;
            this.formData.init({
                employee: this.canShowInSelectBox ? new EmployeeDto({id: employeeId}) : new EmployeeDto()
            });
            this.employeeId.next(employeeId);
        }
    }

    changeStatus(status: ChangeRequestStatus) {
        this.changeRequestsClient.changeRequestStatus(this.formData.id, status).subscribe({
            next: () => this.handleSuccess(),
            error: (errors) => this.handleError(errors)
        });
    }

    protected getBadgeClass(status: ChangeRequestStatus): string {
        switch (status) {
            case ChangeRequestStatus.Pending:
                return 'bg-secondary-subtle text-secondary';
            case ChangeRequestStatus.Approved:
                return 'bg-info-subtle text-info';
            case ChangeRequestStatus.Declined:
                return 'bg-danger-subtle text-danger';
            case ChangeRequestStatus.Withdrawn:
                return 'bg-warning-subtle text-warning';
            case ChangeRequestStatus.Completed:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-info-subtle text-info';
        }
    }

    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;
}
